import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { useCreateOrder } from '../hooks/useOrderMutations';
import { useBomSearch } from '../hooks/useBomSearch';

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

interface LineItem {
  id: string;
  partNumber: string;
  quantity: number;
  promisedShipDate: string;
}

interface OrderCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function createEmptyLineItem(): LineItem {
  return {
    id: crypto.randomUUID(),
    partNumber: '',
    quantity: 1,
    promisedShipDate: '',
  };
}

export function OrderCreateModal({ isOpen, onClose }: OrderCreateModalProps) {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const createMutation = useCreateOrder();

  // Form state
  const [gpSalesOrderNumber, setGpSalesOrderNumber] = useState('');
  const [clientName, setClientName] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([createEmptyLineItem()]);
  const [error, setError] = useState<string | null>(null);

  // Autocomplete state
  const [activeLineItemId, setActiveLineItemId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition | null>(null);
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const {
    data: bomSuggestions = [],
    isLoading: isSearching,
    isFetched: hasSearched,
    error: searchError,
  } = useBomSearch(searchTerm, activeLineItemId !== null);

  const updateDropdownPosition = useCallback((lineItemId: string) => {
    const input = inputRefs.current.get(lineItemId);
    if (input) {
      const rect = input.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setGpSalesOrderNumber('');
      setClientName('');
      setLineItems([createEmptyLineItem()]);
      setError(null);
      setActiveLineItemId(null);
      setSearchTerm('');
      setDropdownPos(null);
    }
  }, [isOpen]);

  // Reposition dropdown on modal scroll
  useEffect(() => {
    const modal = modalContentRef.current;
    if (!modal || !activeLineItemId) return;
    const handleScroll = () => updateDropdownPosition(activeLineItemId);
    modal.addEventListener('scroll', handleScroll);
    return () => modal.removeEventListener('scroll', handleScroll);
  }, [activeLineItemId, updateDropdownPosition]);

  const handleAddLineItem = () => {
    if (lineItems.length >= 15) return;
    setLineItems([...lineItems, createEmptyLineItem()]);
  };

  const handleRemoveLineItem = (id: string) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((li) => li.id !== id));
  };

  const handlePartNumberChange = (id: string, value: string) => {
    setActiveLineItemId(id);
    setSearchTerm(value);
    setLineItems(lineItems.map((li) => (li.id === id ? { ...li, partNumber: value } : li)));
    updateDropdownPosition(id);
  };

  const handlePartNumberSelect = (id: string, partNumber: string) => {
    setLineItems(lineItems.map((li) => (li.id === id ? { ...li, partNumber } : li)));
    setActiveLineItemId(null);
    setSearchTerm('');
    setDropdownPos(null);
  };

  const handlePartNumberBlur = () => {
    // Delay to allow click on suggestion to register
    setTimeout(() => {
      setActiveLineItemId(null);
      setDropdownPos(null);
    }, 200);
  };

  const updateLineItem = (id: string, field: keyof LineItem, value: string | number) => {
    setLineItems(lineItems.map((li) => (li.id === id ? { ...li, [field]: value } : li)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!gpSalesOrderNumber.trim()) {
      setError('Sales Order Number is required');
      return;
    }
    if (!clientName.trim()) {
      setError('Client Name is required');
      return;
    }
    if (lineItems.some((li) => !li.partNumber.trim())) {
      setError('All line items must have a part number');
      return;
    }
    if (lineItems.some((li) => li.quantity < 1)) {
      setError('Quantity must be at least 1');
      return;
    }
    if (lineItems.some((li) => !li.promisedShipDate)) {
      setError('All line items must have a ship date');
      return;
    }

    createMutation.mutate(
      {
        gpSalesOrderNumber: gpSalesOrderNumber.trim(),
        customerId: 'MANUAL',
        customerName: clientName.trim(),
        lineItems: lineItems.map((li) => ({
          partNumber: li.partNumber.trim(),
          quantity: li.quantity,
          promisedShipDate: li.promisedShipDate,
        })),
      },
      {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create order'),
      }
    );
  };

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalContentRef}
        className="relative bg-background rounded-lg shadow-xl w-full max-w-3xl border border-border mx-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Create Order</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-background-muted transition-colors"
          >
            <X className="h-5 w-5 text-foreground-muted" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-4 space-y-4">
            {error && (
              <div className="p-3 rounded-md bg-danger-subtle border border-danger text-sm text-danger-foreground">
                {error}
              </div>
            )}

            {/* Order Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="gpSalesOrderNumber"
                  className="block text-sm font-medium text-foreground-muted mb-1"
                >
                  Sales Order Number
                </label>
                <input
                  id="gpSalesOrderNumber"
                  type="text"
                  value={gpSalesOrderNumber}
                  onChange={(e) => setGpSalesOrderNumber(e.target.value)}
                  placeholder="ORD123456"
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label
                  htmlFor="clientName"
                  className="block text-sm font-medium text-foreground-muted mb-1"
                >
                  Client Name
                </label>
                <input
                  id="clientName"
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Acme Corporation"
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-foreground-muted">
                  Line Items ({lineItems.length}/15)
                </label>
                <button
                  type="button"
                  onClick={handleAddLineItem}
                  disabled={lineItems.length >= 15}
                  className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                  Add Line Item
                </button>
              </div>

              <div className="space-y-2">
                {lineItems.map((lineItem, index) => (
                  <div
                    key={lineItem.id}
                    className="flex gap-2 items-start p-3 border border-border rounded-md bg-background-subtle"
                  >
                    <span className="text-xs text-foreground-muted mt-6 w-4">{index + 1}.</span>
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      {/* Part Number with Autocomplete */}
                      <div>
                        <label className="block text-xs text-foreground-muted mb-1">
                          Part Number
                        </label>
                        <input
                          ref={(el) => {
                            if (el) inputRefs.current.set(lineItem.id, el);
                            else inputRefs.current.delete(lineItem.id);
                          }}
                          type="text"
                          value={lineItem.partNumber}
                          onChange={(e) => handlePartNumberChange(lineItem.id, e.target.value)}
                          onBlur={handlePartNumberBlur}
                          onFocus={() => {
                            setActiveLineItemId(lineItem.id);
                            setSearchTerm(lineItem.partNumber);
                            updateDropdownPosition(lineItem.id);
                          }}
                          placeholder="Search part..."
                          className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>

                      {/* Quantity */}
                      <div>
                        <label className="block text-xs text-foreground-muted mb-1">Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={lineItem.quantity}
                          onChange={(e) =>
                            updateLineItem(lineItem.id, 'quantity', parseInt(e.target.value) || 1)
                          }
                          className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>

                      {/* Ship Date */}
                      <div>
                        <label className="block text-xs text-foreground-muted mb-1">Ship Date</label>
                        <input
                          type="date"
                          value={lineItem.promisedShipDate}
                          onChange={(e) =>
                            updateLineItem(lineItem.id, 'promisedShipDate', e.target.value)
                          }
                          className="w-full px-2 py-1.5 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      type="button"
                      onClick={() => handleRemoveLineItem(lineItem.id)}
                      disabled={lineItems.length === 1}
                      className="mt-6 p-1 text-danger hover:bg-danger-subtle rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover rounded-md transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Create Order
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const showDropdown =
    activeLineItemId !== null &&
    searchTerm.length >= 2 &&
    dropdownPos !== null &&
    (isSearching || hasSearched || searchError);

  const dropdownContent = showDropdown
    ? createPortal(
        <div
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            zIndex: 100,
          }}
          className="bg-background border border-border rounded-md shadow-lg max-h-48 overflow-y-auto"
        >
          {searchError ? (
            <div className="px-3 py-2 text-sm text-danger">Failed to search parts</div>
          ) : isSearching ? (
            <div className="px-3 py-2 text-sm text-foreground-muted">Searching...</div>
          ) : bomSuggestions.length > 0 ? (
            bomSuggestions.map((bom) => (
              <button
                key={bom.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handlePartNumberSelect(activeLineItemId, bom.partNumber)}
                className="w-full text-left px-3 py-2 hover:bg-background-muted text-sm"
              >
                <div className="font-medium text-foreground">{bom.partNumber}</div>
                <div className="text-xs text-foreground-muted truncate">{bom.description}</div>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-foreground-muted">No matching parts found</div>
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      {createPortal(modalContent, document.body)}
      {dropdownContent}
    </>
  );
}
